import { useEffect, useMemo, useRef, useState } from "react";
import "./AreaFormPage.scss";
import { CircularProgress } from "@mui/material";
import { IoChevronBackOutline } from "react-icons/io5";
import { useCountries } from "use-react-countries";
import { useAmplitude } from "../../../../context/AmplitudeContext";
import { useUID } from "../../../../context/UIDContext";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import {
  getNearestStores,
  getZoomLevel,
} from "../../../../services/mapsService";
import stores from "../../../../data/stores.json";

const AllowedCountries = [
  "South Africa",
  "Namibia",
  "Botswana",
  "Eswatini",
  "Lesotho",
];

export const AreaFormPage = ({
  onBackClick,
  address,
  updateAddress,
  updateUserLocation,
  updateNearestStores,
  onContinue,
}) => {
  const { uid } = useUID();
  const trackEvent = useAmplitude();

  const geocodingLib = useMapsLibrary("geocoding");
  const geocoder = useMemo(
    () => geocodingLib && new geocodingLib.Geocoder(),
    [geocodingLib],
  );

  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);
  const { countries } = useCountries();
  const sortedCountries = useMemo(() => {
    return [...countries]
      .filter((country) => AllowedCountries.includes(country.name))
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {
          sensitivity: "base",
        }),
      );
  }, [countries]);

  const handleOnSubmit = () => {
    if (!geocoder) return;
    setIsLoading(true);
    geocoder.geocode(
      {
        address:
          address.postal !== ""
            ? `${address.postal}, ${address.country}`
            : `${address.country}`,
      },
      (results, status) => {
        if (status === "OK") {
          const location = results[0].geometry.location;
          const userLocation = {
            lat: location.lat(),
            lng: location.lng(),
          };

          const nearestStoresResult = getNearestStores(userLocation, stores);

          const graphLocation = {
            lat: nearestStoresResult.stores[0].lat,
            lng: nearestStoresResult.stores[0].lng,
            zoom: getZoomLevel(nearestStoresResult.maxDistance),
          };
          updateUserLocation(graphLocation);
          updateNearestStores(nearestStoresResult.stores);
          setIsLoading(false);
          onContinue();
        } else {
          console.error("Geocode failed:", status);
          setIsLoading(false);
        }
      },
    );
  };

  const handleNumberChange = (e) => {
    const cleaned = e.target.value.replace(/\D/g, "").slice(0, 5);
    updateAddress((prevValue) => ({ ...prevValue, postal: cleaned }));
  };

  const isPostalValid = (postal) => {
    if (!postal) return true;

    const cleaned = postal.replace(/\D/g, "");

    return cleaned.length >= 3 && cleaned.length <= 5;
  };

  const isContinueDisabled = !isPostalValid(address.postal);

  useEffect(() => {
    trackEvent("visited_store_form_page", uid);
  }, [uid, trackEvent]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="area-form-page">
      <div className="area-form-top-background">
        <img
          className="area-form-background-image"
          src="images/curl-yellow.png"
          alt="Yellow Curl"
          width={100}
        />
      </div>
      <div className="area-form-page-header">
        <div className="area-form-back-button-wrapper">
          <div className="back-icon" onClick={onBackClick}>
            <IoChevronBackOutline />
          </div>
        </div>
        <div className="area-form-top-header">
          <img
            src="logo_black.png"
            alt="Perfect Hair"
            className="perfect-hair-logo"
          />
        </div>
      </div>
      <div className="area-form-content-header">
        <h4 className="area-form-title">Enter your area code</h4>
        <p className="area-form-info">
          Tell us your postal code so we can find nearby stores and recommend
          products tailored to you. Not sure? You can leave it empty.
        </p>
      </div>
      <div className="area-form-input-wrapper">
        <div className="phone-input" ref={dropdownRef}>
          <div
            className="selector"
            onClick={() => {
              if (isLoading) return;
              setOpen((prevValue) => !prevValue);
            }}
          >
            <img src={address.flag} className="flag" alt="Country" />
            <span className="arrow">▾</span>
          </div>

          <input
            className="input"
            value={address.postal}
            onChange={handleNumberChange}
            inputMode="numeric"
            disabled={isLoading}
          />

          {open && (
            <div className="dropdown">
              {sortedCountries.map((c) => (
                <div
                  key={c.name}
                  className="item"
                  onClick={() => {
                    updateAddress((prevValue) => ({
                      ...prevValue,
                      country: c.name,
                      flag: c.flags.png,
                    }));
                    setOpen(false);
                  }}
                >
                  <img src={c.flags.png} className="flag" alt="Country" />
                  <span className="name">{c.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="area-form-btn-wrapper-bottom">
        <button
          onClick={handleOnSubmit}
          className="area-form-btn"
          disabled={isLoading || isContinueDisabled}
        >
          {isLoading ? <CircularProgress /> : "Continue"}
        </button>
      </div>
    </div>
  );
};
